import { useEffect, useState } from "react";
import { Loader2, UserCheck, UserX, Sparkles } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * Dropdown for an admin to assign / reassign / unassign an attorney to a lead.
 * Calls onAssigned(updatedLead) so the parent can refresh its state.
 */
export default function AttorneyAssignSelect({ lead, onAssigned }) {
  const [attorneys, setAttorneys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let mounted = true;
    api
      .get("/admin/attorneys")
      .then((r) => {
        if (!mounted) return;
        setAttorneys(r.data.filter((a) => a.active));
      })
      .catch((e) =>
        toast.error(formatApiError(e.response?.data?.detail) || e.message),
      )
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  const assign = async (attorneyId) => {
    setSaving(true);
    try {
      const { data } = await api.patch(
        `/admin/leads/${lead.id}/assign-attorney`,
        { attorney_id: attorneyId },
      );
      onAssigned?.(data);
      toast.success(
        attorneyId ? "Attorney assigned" : "Attorney unassigned",
      );
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card-soft p-4" data-testid="attorney-assign">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="label-cap text-[10px]">Assigned attorney</div>
          <div className="mt-1 inline-flex items-center gap-2 text-sm font-bold text-[#11322A]">
            {lead.attorney_id ? (
              <>
                <UserCheck className="h-4 w-4 text-[#166534]" />
                {lead.attorney_name || "Assigned"}
                {lead.attorney_assigned_method === "auto_city" && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-[#E6F6ED] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#166534]">
                    <Sparkles className="h-2.5 w-2.5" /> Auto by city
                  </span>
                )}
              </>
            ) : (
              <>
                <UserX className="h-4 w-4 text-[#5C6B68]" />
                <span className="text-[#5C6B68]">Unassigned</span>
              </>
            )}
          </div>
        </div>
        {saving && <Loader2 className="h-4 w-4 animate-spin text-[#11322A]" />}
      </div>
      <div className="mt-3 flex items-center gap-2">
        <select
          disabled={loading || saving}
          value={lead.attorney_id || ""}
          onChange={(e) => assign(e.target.value || null)}
          className="input-field !w-auto flex-1 text-sm disabled:opacity-50"
          data-testid="attorney-assign-select"
        >
          <option value="">— Unassigned —</option>
          {attorneys.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
              {a.firm_name ? ` · ${a.firm_name}` : ""}
              {a.case_counts?.open != null ? ` (${a.case_counts.open} open)` : ""}
            </option>
          ))}
        </select>
      </div>
      {attorneys.length === 0 && !loading && (
        <p className="mt-2 text-[11px] text-[#5C6B68]" data-testid="attorney-assign-empty">
          No active attorneys yet — invite one from the Attorneys panel.
        </p>
      )}
    </div>
  );
}
